import {
  APIResponse,
  TwilioVerificationChannelEnum,
  User,
  useToggle,
  useUserCheckEmailExistsMutation,
  useTwilioSendCodeMutation,
  forgotPasswordValidation,
} from '@alluradirect/react';
import { Typography } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import Grid from 'components/core/Grid';
import { LoadingButton } from 'components/core/LoadingButton/LoadingButton';
import { FormikAppEmail } from 'components/formik/core/FormikAppEmail/FormikAppEmail';
import { ForgotPasswordFormikValues } from 'components/views/pages/forgot-password/initialValues';
import { VerificationDialog, VerificationStepEnum } from 'components/views/verification/VerificationDialog';
import { Form, Formik, FormikHelpers } from 'formik';
import { Dispatch, SetStateAction, useState } from 'react';
import { AppLinkRoot } from 'components/core/AppLink/AppLink';
import { userNextRouteTransitions } from 'hooks/useNextRouteTransitions';
import * as Sentry from '@sentry/nextjs';
import { toast } from 'react-toastify';
import AlluraTurnstile from 'components/layout/Page/components/AlluraTurnstile';
import { verifyTurnstileToken } from 'components/views/pages/_general/helpers';

interface Props {
  hasTopAppDivider?: boolean;
  setVerified: Dispatch<SetStateAction<boolean>>;
  setUser: Dispatch<SetStateAction<null | User>>;
  initialForgotPasswordValues: ForgotPasswordFormikValues;
}

export const ForgotPasswordForm = ({
  hasTopAppDivider = true,
  setVerified,
  setUser,
  initialForgotPasswordValues,
}: Props) => {
  const toggleVerification = useToggle();
  const sendCode = useTwilioSendCodeMutation();
  const { navigateToGuestSignUp } = userNextRouteTransitions();
  const checkEmailExists = useUserCheckEmailExistsMutation();
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const onSubmit = async (values: any, formikHelpers: FormikHelpers<any>) => {
    const { setFieldError, setSubmitting } = formikHelpers;
    try {
      setSubmitting(true);

      if (!captchaToken) {
        toast.error("Please complete the safety check.");
        return;
      }

      const turnstileData = await verifyTurnstileToken(captchaToken, 'forgotpassword');

      if (!turnstileData?.SUCCESS || !turnstileData?.DATA?.SUCCESS) {
        toast.error("Server rejected the request or captcha failed.");
        return;
      }

      const res = await checkEmailExists.mutateAsync({ urlArgs: [values.email] });
      if (!res || !res?.DATA) {
        const errorMessage = 'Server Error - Please try again';
        setFieldError('email', errorMessage);
        toast.error(errorMessage, { toastId: errorMessage });
        return;
      }
      if (!res.DATA.IS_EMAIL_TAKEN) {
        const errorMessage = 'No user account exists - create an account';
        setFieldError('email', errorMessage);
        toast.error(errorMessage, { toastId: errorMessage });
        return;
      }

      await sendCode.mutateAsync({
        value: values.email,
        channel: TwilioVerificationChannelEnum.email,
      });
      toggleVerification.open();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifySuccess = (data: APIResponse<{ USER: User }>) => {
    setVerified(true);
    setUser(data.DATA.USER);
  };

  return (
    <Formik onSubmit={onSubmit} initialValues={initialForgotPasswordValues} validationSchema={forgotPasswordValidation}>
      {({ isSubmitting, values }) => (
        <Form>
          <Grid container spacing={2}>
            {hasTopAppDivider ? (
              <Grid item xs={12}>
                <AppDivider />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <Typography variant="body1">
                Enter your email, and we will send you an email with a verification code to reset your password.
              </Typography>
              <AppDivider marginVertical='16px' />
            </Grid>

            <Grid item xs={12}>
              <FormikAppEmail name="email" />
            </Grid>
            <Grid item xs={12}>
              <AlluraTurnstile onVerify={(token) => setCaptchaToken(token)} siteKey={process.env.NEXT_PUBLIC_TURNSTILE_FORGOTPASSWORD_KEY ?? ''} />
            </Grid>
            <Grid item xs={12}>
              <LoadingButton
                isDisabled={!captchaToken}
                isLoading={isSubmitting || sendCode.isLoading}
                text="Reset Password"
                isFullWidth
                size="large"
                isFormikButton
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" align="center">
                - or -
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <AppLinkRoot onClick={navigateToGuestSignUp} isButtonLink>
                Create Guest Account
              </AppLinkRoot>
            </Grid>
          </Grid>

          {toggleVerification.isOpen ? (
            <VerificationDialog
              initialData={values.email}
              channel={TwilioVerificationChannelEnum.email}
              handleClose={toggleVerification.close}
              isOpen={toggleVerification.isOpen}
              initialStep={VerificationStepEnum.VerifyCode}
              onVerifySuccess={handleVerifySuccess}
              autoLoginOnVerificationSuccess
            />
          ) : null}
        </Form>
      )}
    </Formik>
  );
};
